import { Table, CombinedTable } from "@/types/table";

/**
 * Extracts the common prefix from a table name (e.g., "T41" from "T41a")
 * Returns the full name if no pattern is detected
 */
export function extractTablePrefix(tableName: string): { prefix: string; suffix: string } {
  // Match patterns like "T41a", "Table 1a", "T1-a", etc.
  const match = tableName.match(/^(.+?)([a-zA-Z])$/);
  if (match) {
    return { prefix: match[1].trim(), suffix: match[2].toLowerCase() };
  }
  // Try numeric suffix like "T41 1", "T41-1"
  const numMatch = tableName.match(/^(.+?)[\s\-_]?(\d+)$/);
  if (numMatch) {
    return { prefix: numMatch[1].trim(), suffix: numMatch[2] };
  }
  return { prefix: tableName, suffix: "" };
}

/**
 * Groups tables by their common prefix
 */
export function groupTablesByPrefix(tables: Table[]): Map<string, Table[]> {
  const groups = new Map<string, Table[]>();
  
  tables.forEach((table) => {
    const { prefix, suffix } = extractTablePrefix(table.name);
    
    // Only group if there's a suffix (meaning it's part of a group)
    if (suffix) {
      const existing = groups.get(prefix) || [];
      existing.push(table);
      groups.set(prefix, existing);
    }
  });
  
  // Remove groups with only 1 table (can't combine alone)
  const validGroups = new Map<string, Table[]>();
  groups.forEach((tablesInGroup, prefix) => {
    if (tablesInGroup.length >= 2) {
      // Sort by suffix (alphabetically or numerically)
      tablesInGroup.sort((a, b) => {
        const { suffix: suffixA } = extractTablePrefix(a.name);
        const { suffix: suffixB } = extractTablePrefix(b.name);
        return suffixA.localeCompare(suffixB, undefined, { numeric: true });
      });
      validGroups.set(prefix, tablesInGroup);
    }
  });
  
  return validGroups;
}

export interface TableCombination {
  tables: Table[];
  tableIds: string[];
  name: string;
  totalCapacity: number; // Capacité calculée (somme des tables)
  effectiveCapacity?: number; // Capacité effective définie par l'utilisateur
  alreadyExists: boolean;
}

/**
 * Generates all consecutive combinations for an ordered list of tables
 * For [a, b, c, d]:
 * - Size 2: a+b, b+c, c+d
 * - Size 3: a+b+c, b+c+d
 * - Size 4: a+b+c+d
 */
export function generateConsecutiveCombinations(
  orderedTables: Table[],
  existingCombinations: CombinedTable[]
): TableCombination[] {
  const combinations: TableCombination[] = [];
  const n = orderedTables.length;
  
  // For each size of combination (2, 3, 4, ...)
  for (let size = 2; size <= n; size++) {
    // For each starting position
    for (let start = 0; start <= n - size; start++) {
      const tablesSlice = orderedTables.slice(start, start + size);
      const tableIds = tablesSlice.map(t => t.id).sort();
      const name = tablesSlice.map(t => t.name).join(" + ");
      const totalCapacity = tablesSlice.reduce((sum, t) => sum + t.capacity, 0);
      
      // Check if this combination already exists
      const alreadyExists = existingCombinations.some(ct => {
        const existingIds = [...ct.tableIds].sort();
        return existingIds.length === tableIds.length && 
               existingIds.every((id, idx) => id === tableIds[idx]);
      });
      
      combinations.push({
        tables: tablesSlice,
        tableIds: tablesSlice.map(t => t.id), // Keep original order for name
        name,
        totalCapacity,
        alreadyExists,
      });
    }
  }
  
  return combinations;
}

/**
 * Counts new and existing combinations from a list
 */
export function countCombinations(combinations: TableCombination[]): {
  newCount: number;
  existingCount: number;
  total: number;
} {
  const existingCount = combinations.filter(c => c.alreadyExists).length;
  const newCount = combinations.length - existingCount;
  return { newCount, existingCount, total: combinations.length };
}
